"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.bankSummaryQuerySchema = exports.cashSummaryQuerySchema = exports.bankBookQuerySchema = exports.cashBookQuerySchema = exports.cancelChequeRequestSchema = exports.bounceChequeRequestSchema = exports.clearChequeRequestSchema = exports.issueChequeRequestSchema = exports.receiveChequeRequestSchema = exports.rejectCashBankRequestSchema = exports.cashBankWorkflowActionRequestSchema = exports.createBankTransferRequestSchema = exports.createCashTransferRequestSchema = exports.createBankWithdrawalRequestSchema = exports.createBankDepositRequestSchema = exports.closeCashSessionRequestSchema = exports.cashDenominationLineSchema = exports.openCashSessionRequestSchema = exports.updateCashCounterRequestSchema = exports.createCashCounterRequestSchema = exports.createBankAccountRequestSchema = void 0;
/**
 * Request contracts for Cash & Bank Management (Stage 13, Milestone 2
 * Part 3) — `BankAccountApplicationService`/`CashCounterApplicationService`/
 * `CashSessionApplicationService`/`BankDepositApplicationService`/
 * `BankWithdrawalApplicationService`/`CashTransferApplicationService`/
 * `BankTransferApplicationService`/`ChequeApplicationService`
 * (`backend/src/modules/financial/cash-bank/`).
 */
const zod_1 = require("zod");
const counter_1 = require("../../domain/counter");
const uhid_counter_1 = require("../../domain/uhid-counter");
const financial_common_request_1 = require("./financial-common.request");
exports.createBankAccountRequestSchema = zod_1.z.object({
    accountNumber: zod_1.z.string().min(1).max(50),
    bankName: zod_1.z.string().min(1).max(255),
    branchName: zod_1.z.string().max(255).optional(),
    ifscCode: zod_1.z.string().max(20).optional(),
    currency: zod_1.z.string().max(10).optional(),
    openingBalance: zod_1.z.number().int().nonnegative().optional(),
    openingDate: zod_1.z.coerce.date().optional(),
    glAccountId: zod_1.z.string().uuid(),
    isPrimary: zod_1.z.boolean().optional(),
    ...financial_common_request_1.financialWorkflowExtrasShape,
});
/**
 * ADR-066 Counter Master. `counterType` was a free-text VarChar until
 * this change; it is now the closed `COUNTER_TYPES` enum, because the
 * settlement engine branches on it. Every other field is optional and
 * column-defaulted, so an existing caller that sends only
 * branch/code/name still creates a valid counter.
 *
 * `parentCounterId` is deliberately NOT required here even though every
 * non-Main counter needs one — the rule is conditional on `counterType`
 * (see `counterRequiresParent`), and a conditional required-ness that
 * Zod expresses as a refinement produces a worse error message than the
 * service's own explicit ConflictError. The service is the enforcement
 * point; this schema is the shape.
 */
const cashCounterConfigShape = {
    counterType: zod_1.z.enum(counter_1.COUNTER_TYPES).optional(),
    workstationModule: zod_1.z.enum(counter_1.COUNTER_WORKSTATIONS).optional(),
    /**
     * Both of these are real columns that the request shape had simply never
     * exposed, so no caller could set them and the admin form had nothing to
     * bind to. Added when the counter master gained an edit screen.
     */
    description: zod_1.z.string().max(2000).nullable().optional(),
    allowsMultipleCashiers: zod_1.z.boolean().optional(),
    departmentId: zod_1.z.string().uuid().nullable().optional(),
    parentCounterId: zod_1.z.string().uuid().nullable().optional(),
    building: zod_1.z.string().max(100).nullable().optional(),
    floor: zod_1.z.string().max(50).nullable().optional(),
    costCenterId: zod_1.z.string().uuid().nullable().optional(),
    defaultCashAccountId: zod_1.z.string().uuid().nullable().optional(),
    defaultBankAccountId: zod_1.z.string().uuid().nullable().optional(),
    cashDrawerEnabled: zod_1.z.boolean().optional(),
    receiptPrinter: zod_1.z.string().max(120).nullable().optional(),
    tokenPrinter: zod_1.z.string().max(120).nullable().optional(),
    labelPrinter: zod_1.z.string().max(120).nullable().optional(),
    barcodePrinter: zod_1.z.string().max(120).nullable().optional(),
    allowCash: zod_1.z.boolean().optional(),
    allowCard: zod_1.z.boolean().optional(),
    allowUpi: zod_1.z.boolean().optional(),
    allowCheque: zod_1.z.boolean().optional(),
    allowNeft: zod_1.z.boolean().optional(),
    allowCorporate: zod_1.z.boolean().optional(),
    allowInsurance: zod_1.z.boolean().optional(),
    allowCredit: zod_1.z.boolean().optional(),
    /** Minor units (paise), matching `openingBalance` everywhere else in this file. */
    maxCashLimit: zod_1.z.number().int().nonnegative().nullable().optional(),
    shiftMandatory: zod_1.z.boolean().optional(),
    settlementMandatory: zod_1.z.boolean().optional(),
    openingBalanceRequired: zod_1.z.boolean().optional(),
    openingFloat: zod_1.z.number().int().nonnegative().optional(),
    cashDepositFrequency: zod_1.z.enum(counter_1.CASH_DEPOSIT_FREQUENCIES).optional(),
    currency: zod_1.z.string().min(1).max(10).optional(),
    displayOrder: zod_1.z.number().int().min(0).optional(),
    /**
     * Which registration series this desk may issue a UHID from. An EMPTY array
     * and `null` both mean "all three" — see `allowedUhidCounterTypes`. Sending
     * `[]` is therefore how an administrator lifts a restriction, and it must
     * stay distinguishable from omitting the field, which changes nothing.
     */
    uhidCounterTypes: zod_1.z.array(zod_1.z.enum(uhid_counter_1.UHID_COUNTER_TYPES)).nullable().optional(),
};
exports.createCashCounterRequestSchema = zod_1.z.object({
    branchId: zod_1.z.string().uuid(),
    code: zod_1.z.string().min(1).max(20),
    name: zod_1.z.string().min(1).max(255),
    ...cashCounterConfigShape,
    ...financial_common_request_1.financialWorkflowExtrasShape,
});
/** Everything but `branchId` is editable — moving a counter to another branch would silently re-attribute its historic sessions, deposits and invoices. */
exports.updateCashCounterRequestSchema = zod_1.z.object({
    code: zod_1.z.string().min(1).max(20).optional(),
    name: zod_1.z.string().min(1).max(255).optional(),
    ...cashCounterConfigShape,
    ...financial_common_request_1.financialWorkflowExtrasShape,
});
exports.openCashSessionRequestSchema = zod_1.z.object({
    branchId: zod_1.z.string().uuid(),
    counterId: zod_1.z.string().uuid(),
    assignedUserId: zod_1.z.string().uuid(),
    /**
     * Counted cash in the drawer, in paise. Optional: a counter with
     * `openingBalanceRequired` off falls back to its configured `openingFloat`.
     * A counter that requires it and does not receive it is refused — note that
     * a declared `0` satisfies the requirement, since an empty drawer is a real
     * count and not a missing one.
     */
    openingBalance: zod_1.z.number().int().nonnegative().optional(),
    ...financial_common_request_1.financialWorkflowExtrasShape,
});
/**
 * One line of a denomination sheet: a face value, and how many of them were
 * counted. `value` is in PAISE so it shares the unit of every other money field.
 */
exports.cashDenominationLineSchema = zod_1.z.object({
    value: zod_1.z.number().int().positive(),
    count: zod_1.z.number().int().nonnegative(),
});
exports.closeCashSessionRequestSchema = zod_1.z
    .object({
    /**
     * The counted total, in paise.
     *
     * OPTIONAL WHEN `denominations` IS GIVEN — the server derives it from the
     * sheet in that case, because the browser must not be the thing that
     * decides how much money was counted. Supplying both is allowed and is
     * CHECKED: a mismatch means the sheet and the total disagree, which is the
     * one error worth refusing loudly rather than silently preferring one.
     */
    countedClosingBalance: zod_1.z.number().int().nonnegative().optional(),
    /** The count itself. Stored on the settlement snapshot as the working behind the total. */
    denominations: zod_1.z.array(exports.cashDenominationLineSchema).optional(),
    ...financial_common_request_1.financialWorkflowExtrasShape,
})
    .refine((value) => value.countedClosingBalance !== undefined || value.denominations !== undefined, {
    message: 'Provide the counted amount, a denomination sheet, or both',
    path: ['countedClosingBalance'],
});
exports.createBankDepositRequestSchema = zod_1.z.object({
    bankAccountId: zod_1.z.string().uuid(),
    counterId: zod_1.z.string().uuid(),
    amount: zod_1.z.number().int().positive(),
    referenceNumber: zod_1.z.string().max(100).optional(),
    depositSlipUrl: zod_1.z.string().max(2000).optional(),
    ...financial_common_request_1.financialWorkflowExtrasShape,
});
exports.createBankWithdrawalRequestSchema = zod_1.z.object({
    bankAccountId: zod_1.z.string().uuid(),
    counterId: zod_1.z.string().uuid(),
    amount: zod_1.z.number().int().positive(),
    reason: zod_1.z.string().optional(),
    referenceNumber: zod_1.z.string().max(100).optional(),
    ...financial_common_request_1.financialWorkflowExtrasShape,
});
exports.createCashTransferRequestSchema = zod_1.z.object({
    fromCounterId: zod_1.z.string().uuid(),
    toCounterId: zod_1.z.string().uuid(),
    amount: zod_1.z.number().int().positive(),
    reason: zod_1.z.string().optional(),
    ...financial_common_request_1.financialWorkflowExtrasShape,
});
exports.createBankTransferRequestSchema = zod_1.z.object({
    fromBankAccountId: zod_1.z.string().uuid(),
    toBankAccountId: zod_1.z.string().uuid(),
    amount: zod_1.z.number().int().positive(),
    reason: zod_1.z.string().optional(),
    ...financial_common_request_1.financialWorkflowExtrasShape,
});
/** Shared by BankWithdrawal/CashTransfer/BankTransfer submit/complete actions — no extra fields beyond the workflow envelope. */
exports.cashBankWorkflowActionRequestSchema = zod_1.z.object({
    ...financial_common_request_1.financialWorkflowExtrasShape,
});
exports.rejectCashBankRequestSchema = zod_1.z.object({
    reason: zod_1.z.string().min(1),
    ...financial_common_request_1.financialWorkflowExtrasShape,
});
exports.receiveChequeRequestSchema = zod_1.z.object({
    chequeNumber: zod_1.z.string().min(1).max(30),
    chequeDate: zod_1.z.coerce.date(),
    bankName: zod_1.z.string().max(255).optional(),
    patientId: zod_1.z.string().uuid(),
    amount: zod_1.z.number().int().positive(),
    ...financial_common_request_1.financialWorkflowExtrasShape,
});
exports.issueChequeRequestSchema = zod_1.z.object({
    chequeNumber: zod_1.z.string().min(1).max(30),
    chequeDate: zod_1.z.coerce.date(),
    bankName: zod_1.z.string().max(255).optional(),
    vendorId: zod_1.z.string().uuid(),
    amount: zod_1.z.number().int().positive(),
    ...financial_common_request_1.financialWorkflowExtrasShape,
});
exports.clearChequeRequestSchema = zod_1.z.object({
    clearedIntoAccountType: zod_1.z.enum(['CASH', 'BANK']),
    clearedIntoBankAccountId: zod_1.z.string().uuid().optional(),
    ...financial_common_request_1.financialWorkflowExtrasShape,
});
exports.bounceChequeRequestSchema = zod_1.z.object({
    reason: zod_1.z.string().min(1),
    ...financial_common_request_1.financialWorkflowExtrasShape,
});
exports.cancelChequeRequestSchema = zod_1.z.object({
    reason: zod_1.z.string().optional(),
    ...financial_common_request_1.financialWorkflowExtrasShape,
});
exports.cashBookQuerySchema = zod_1.z.object({
    counterId: zod_1.z.string().uuid(),
    date: zod_1.z.coerce.date().optional(),
});
exports.bankBookQuerySchema = zod_1.z.object({
    bankAccountId: zod_1.z.string().uuid(),
    from: zod_1.z.coerce.date().optional(),
    to: zod_1.z.coerce.date().optional(),
});
exports.cashSummaryQuerySchema = zod_1.z.object({
    branchId: zod_1.z.string().uuid().optional(),
    from: zod_1.z.coerce.date().optional(),
    to: zod_1.z.coerce.date().optional(),
});
exports.bankSummaryQuerySchema = zod_1.z.object({
    bankAccountId: zod_1.z.string().uuid().optional(),
});
//# sourceMappingURL=financial-cash-bank.request.js.map